import React from "react"
import config from "aldoo-ra/Config"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Badge from "aldoo-ra/Badge"

function IndexPage() {
  const appName = config.APP_NAME || "My Aldoo-Ra Project"
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-canvas via-neutral to-paper relative overflow-hidden">
      {/* Enhanced Background with Modern Gradients */}
      <div className="absolute inset-0">
        {/* Multiple gradient layers */}
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/8 via-transparent to-accent/8"></div>
        <div className="absolute inset-0 bg-gradient-to-bl from-secondary/6 via-transparent to-primary/6"></div>
        
        {/* Animated floating orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-10 w-96 h-96 bg-gradient-to-l from-accent/15 to-primary/15 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-1/3 w-64 h-64 bg-gradient-to-t from-secondary/25 to-accent/25 rounded-full blur-2xl animate-pulse delay-500"></div>
        
        {/* Subtle mesh pattern */}
        <div className="absolute inset-0 opacity-[0.02] bg-gradient-to-r from-text via-transparent to-text" 
             style={{backgroundImage: 'radial-gradient(circle at 50% 50%, currentColor 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
      </div>
      
      {/* Hero Section */}
      <div className="relative flex flex-col items-center justify-center min-h-screen px-4 pt-20">
        {/* Status Badge */}
        <div className="mb-8">
          <Badge variant="success" className="animate-bounce">
            🚀 Project Ready
          </Badge>
        </div>
        
        {/* Main Content */}
        <div className="relative z-10 max-w-5xl text-center">
          {/* Main Headline with enhanced gradient */}
          <Typography 
            variant="h1" 
            className="mb-8 bg-gradient-to-r from-primary via-secondary via-accent to-primary bg-clip-text text-transparent animate-pulse bg-[length:200%_100%]"
          >
            {appName}
          </Typography>
          
          {/* Subheading */}
          <Typography 
            variant="h3" 
            className="mb-10 text-text max-w-4xl mx-auto leading-relaxed"
          >
            Your Project is Ready to Build Amazing Things
          </Typography>
          
          {/* Description with better spacing */}
          <Typography 
            variant="body_24" 
            className="mb-16 text-text_secondary max-w-3xl mx-auto leading-relaxed"
          >
            This is your freshly set up Aldoo-Ra project. The framework is loaded, 
            components are ready, and your development environment is configured. 
            Time to start building something extraordinary.
          </Typography>
          
          {/* Enhanced CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-24">
            <Button variant="primary_l" className="transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-2xl">
              Get Started
            </Button>
            <Button variant="secondary" className="transform hover:scale-105 transition-all duration-300">
              View Documentation
            </Button>
          </div>
          
          {/* Tech Stack Pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-20">
            <Badge variant="info" className="px-4 py-2 text-sm">React</Badge>
            <Badge variant="success" className="px-4 py-2 text-sm">Vite</Badge>
            <Badge variant="warning" className="px-4 py-2 text-sm">Tailwind</Badge>
            <Badge variant="default" className="px-4 py-2 text-sm">aldoo-ra</Badge>
          </div>
        </div>
      </div>

      {/* Features Section with Enhanced Design */}
      <div className="bg-gradient-to-b from-paper to-canvas py-24 px-4 relative">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full blur-2xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-gradient-to-tl from-accent/20 to-primary/20 rounded-full blur-2xl"></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-20">
            <Typography variant="h2" className="mb-6 text-text bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              What's Included
            </Typography>
            <Typography variant="body_18" className="text-text_secondary max-w-2xl mx-auto">
              Everything you need to build modern web applications with confidence
            </Typography>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 - Enhanced */}
            <div className="group text-center p-8 rounded-2xl bg-gradient-to-b from-neutral/50 to-canvas/50 backdrop-blur-sm border border-neutral_2/20 hover:border-primary/30 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary_dk rounded-xl"></div>
              </div>
              <Typography variant="h4" className="mb-6 text-text group-hover:text-primary transition-colors">
                Component System
              </Typography>
              <Typography variant="body_16" className="text-text_secondary leading-relaxed">
                Pre-built Typography, Button, Badge, and layout components with consistent variants and modern styling.
              </Typography>
            </div>
            
            {/* Feature 2 - Enhanced */}
            <div className="group text-center p-8 rounded-2xl bg-gradient-to-b from-neutral/50 to-canvas/50 backdrop-blur-sm border border-neutral_2/20 hover:border-secondary/30 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="w-20 h-20 bg-gradient-to-br from-secondary/20 to-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <div className="w-10 h-10 bg-gradient-to-br from-secondary to-secondary_dk rounded-xl"></div>
              </div>
              <Typography variant="h4" className="mb-6 text-text group-hover:text-secondary transition-colors">
                Modern Stack
              </Typography>
              <Typography variant="body_16" className="text-text_secondary leading-relaxed">
                React + Vite + Vike for lightning-fast development with server-side rendering and modern tooling.
              </Typography>
            </div>
            
            {/* Feature 3 - Enhanced */}
            <div className="group text-center p-8 rounded-2xl bg-gradient-to-b from-neutral/50 to-canvas/50 backdrop-blur-sm border border-neutral_2/20 hover:border-accent/30 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="w-20 h-20 bg-gradient-to-br from-accent/20 to-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <div className="w-10 h-10 bg-gradient-to-br from-accent to-accent_dk rounded-xl"></div>
              </div>
              <Typography variant="h4" className="mb-6 text-text group-hover:text-accent transition-colors">
                Ready to Deploy
              </Typography>
              <Typography variant="body_16" className="text-text_secondary leading-relaxed">
                Configured build process, optimized assets, and deployment-ready setup out of the box.
              </Typography>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Spacing for Footer */}
      <div className="h-96 bg-gradient-to-t from-canvas to-paper relative overflow-hidden">
        {/* Decorative bottom section */}
        <div className="absolute inset-0">
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-neutral/10 to-transparent"></div>
          <div className="absolute bottom-10 left-1/4 w-64 h-64 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-1/4 w-48 h-48 bg-gradient-to-l from-accent/5 to-primary/5 rounded-full blur-2xl"></div>
        </div>
        
        {/* Call to action in footer space */}
        <div className="relative flex flex-col items-center justify-center h-full px-4">
          <Typography variant="h3" className="mb-6 text-text text-center max-w-2xl">
            Ready to Start Building?
          </Typography>
          <Typography variant="body_18" className="mb-8 text-text_secondary text-center max-w-xl">
            Edit <code className="bg-neutral px-2 py-1 rounded text-primary font-mono text-sm">components/Site/IndexPage.jsx</code> to customize this page and make it your own.
          </Typography>
          <Button variant="primary_m" className="transform hover:scale-110 transition-all duration-300">
            Start Coding
          </Button>
        </div>
      </div>
    </div>
  )
}

export default IndexPage